import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mic, Monitor, Loader2 } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";
import type { RecordingSource, RecordingTypeDialogProps } from "@/types/recording";

export function RecordingTypeDialog({
  open,
  onOpenChange,
  onSelect,
  isCreating,
}: RecordingTypeDialogProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<RecordingSource | null>(null);

  const handlePick = (source: RecordingSource) => {
    if (isCreating) return;
    setPending(source);
    onSelect(source);
  };

  const options: Array<{
    source: RecordingSource;
    title: string;
    desc: string;
    Icon: typeof Mic;
  }> = [
    {
      source: "microphone",
      title: t("recording.type.mic.title"),
      desc: t("recording.type.mic.desc"),
      Icon: Mic,
    },
    {
      source: "browser-tab",
      title: t("recording.type.tab.title"),
      desc: t("recording.type.tab.desc"),
      Icon: Monitor,
    },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setPending(null);
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("recording.type.title")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("recording.type.title")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2.5 pt-1">
          {options.map(({ source, title, desc, Icon }) => {
            const isPending = pending === source && isCreating;
            return (
              <button
                key={source}
                type="button"
                disabled={isCreating}
                onClick={() => handlePick(source)}
                aria-label={title}
                className={cn(
                  "group flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left transition-colors",
                  "hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  "disabled:cursor-not-allowed disabled:opacity-60",
                )}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted">
                  {isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin text-foreground" />
                  ) : (
                    <Icon className="h-5 w-5 text-foreground" aria-hidden />
                  )}
                </div>
                <div className="flex min-w-0 flex-col">
                  <span className="text-sm font-medium text-foreground">{title}</span>
                  <span className="text-xs text-muted-foreground">{desc}</span>
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
